import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Play, Target } from 'lucide-react';
import {
  BudgetBar,
  Card,
  PrepaidBar,
  type PrepaidHoursLike,
  DataTable,
  InfoHint,
  StatusPill,
  toast,
  type DataTableColumn,
  type DataTableGroup,
  type DataTableSort,
} from '../../components/ui';
import { useOrgSettings } from '../settings/api';
import { useStartTimer } from '../time/api';
import { cn } from '../../lib/cn';
import { formatHours, formatMoney } from '../../lib/format';
import {
  BASIS_LABEL,
  HubBadge, UnlinkedBadge,
  MarginText,
  REVENUE_NOUN,
  REVENUE_SOURCE_TAG,
  RollupBasisInfo,
  rollupBasisNote,
  TypeBadge,
  VerdictChip,
  type RevenueSource,
} from './components';
import { verdictClass, verdictScore, verdictSummary } from './verdict';
import type { MetricsBasis, AgreementMetrics, AgreementType } from './api';

/**
 * THE agreements instrument — one table, wherever a set of agreements is listed.
 *
 * /agreements and a client's Agreements tab used to be two different products: a
 * seven-column table with cost, burn, margin and a two-line footer on one side,
 * and a 2-up card grid with no cost and no total on the other. The client page
 * therefore could not answer "is this client profitable?" — the entire
 * differentiator evaporated one click off /agreements. Both now render this.
 *
 * It reads the server's `metrics` object verbatim (see
 * backend/src/modules/agreements/agreement-metrics.service.ts) and re-derives
 * nothing except the footer sums.
 */

export interface AgreementTableRow {
  id: string;
  name: string;
  code: string | null;
  type: AgreementType;
  isActive: boolean;
  metrics: AgreementMetrics;
  /** Absent inside a single client's page — the column is dropped there. */
  client?: { id: string; name: string };
  /** Set = linked to a hub project (Wave C2). Renders the Hub badge beside the
   *  name so the list says which agreements the hub also knows about. Optional:
   *  callers whose API does not carry the link key simply show no badge. */
  hubProjectIds?: string[];
  /** Prepaid hours the agreement owns. When present the burn column shows
   *  THESE (hours used of hours bought, and hours left) instead of the budget
   *  burn — they are the figures the agreement page's Prepaid hours card
   *  prints. Optional: callers whose API does not carry it show the budget. */
  prepaid?: PrepaidHoursLike | null;
}

/** Share of the prepaid hours used — the burn column's sort key when prepaid. */
const prepaidPct = (p: PrepaidHoursLike) => (p.purchased > 0 ? p.burnt / p.purchased : null);

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

export interface RollupBucket {
  count: number;
  hours: number;
  revenue: number;
  cost: number;
}

export interface AgreementsRollup {
  /** Billable bases only — the margin anyone quotes. */
  client: RollupBucket;
  /** NON_BILLABLE agreements: cost, no revenue. */
  internal: RollupBucket;
  all: RollupBucket;
  clientMargin: number | null;
  loadedMargin: number | null;
  unbilled: number;
  /**
   * The distinct bases the client revenue total is made of, so the footer can
   * name them the way every row already does (see `rollupBasisNote`). It is
   * derived here rather than at each call site: the header, the desktop
   * `<tfoot>` and the phone card must not be able to describe one figure three
   * ways.
   */
  sources: RevenueSource[];
}

/**
 * Client work and internal overhead are totalled SEPARATELY.
 *
 * Lumping ~$591k of internal/non-billable cost in with client work produced a
 * −14.7% "blended margin" in the footer, unlabelled and contradicting the
 * header. Both numbers are real and both are worth seeing — they just are not
 * the same number. (Mirrors `rollupMetrics` in the backend metrics service, so
 * the client page's server-computed rollup and this footer agree.)
 */
export function rollupAgreements(rows: AgreementTableRow[]): AgreementsRollup {
  const zero = (): RollupBucket => ({ count: 0, hours: 0, revenue: 0, cost: 0 });
  const client = zero();
  const internal = zero();
  const sources = new Set<RevenueSource>();
  let unbilled = 0;
  for (const p of rows) {
    const bucket = p.metrics.basis === 'NON_BILLABLE' ? internal : client;
    if (bucket === client && p.metrics.revenueSource !== 'NONE') {
      sources.add(p.metrics.revenueSource);
    }
    bucket.count += 1;
    bucket.hours += p.metrics.hours;
    bucket.revenue += p.metrics.revenue;
    bucket.cost += p.metrics.cost;
    // `unbilledTotal`, mirroring rollupMetrics on the server: the hours the
    // wizard would price AND the billable expenses it would pull on with them.
    unbilled += p.metrics.unbilledTotal;
  }
  const all = {
    count: client.count + internal.count,
    hours: client.hours + internal.hours,
    revenue: client.revenue + internal.revenue,
    cost: client.cost + internal.cost,
  };
  const marginOf = (t: RollupBucket) => (t.revenue > 0 ? (t.revenue - t.cost) / t.revenue : null);
  return {
    client,
    internal,
    all,
    clientMargin: marginOf(client),
    loadedMargin: marginOf(all),
    unbilled,
    sources: [...sources],
  };
}

/** Tooltip on every money cell — the row says what its own numbers mean. */
const revenueTitle = (basis: MetricsBasis) => `${BASIS_LABEL[basis]} — ${REVENUE_NOUN[basis]}`;

const COST_TITLE =
  'Delivery cost: tracked time at each person’s cost rate, plus agreement expenses. Salaried people are costed by spreading their monthly salary across every hour they logged that month across all agreements — so an agreement’s cost moves as its team tracks time elsewhere in the same month.';

const BURN_TITLE =
  'Hours/fee consumed. Monthly-fee agreements burn one month at a time — every such cell names its month and carries the all-time figure ("life") beside it.';

const STATUS_TITLE =
  'The same verdict the agreement page leads with — over budget, thin margin, losing money — arbitrated once from revenue, cost, margin and lifetime burn. Sort by it (the default) to put the portfolio’s worst problem in row 1; hover a pill for the sentence behind it.';

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

export function agreementColumns(opts: {
  /** Render the client name under the agreement name (list view only). */
  showClient: boolean;
  /** Inline "no budget → set one". Falls back to a link to the agreement. */
  onSetBudget?: (row: AgreementTableRow) => void;
  /** Per-row start-timer / quick-invoice column (list view only). */
  rowActions?: boolean;
  /** Org policy on: a live unlinked row wears the required badge. */
  requireHubLink?: boolean;
}): Array<DataTableColumn<AgreementTableRow>> {
  return [
    {
      key: 'name',
      header: 'Agreement',
      sortable: true,
      sortValue: (p) => p.name,
      render: (p) => (
        <div className="min-w-[9rem] max-w-[14rem] leading-tight">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold text-secondary">{p.name}</span>
            {(p.hubProjectIds?.length ?? 0) > 0 ? (
              <HubBadge />
            ) : opts.requireHubLink && p.isActive ? (
              <UnlinkedBadge />
            ) : null}
            {!p.isActive && <StatusPill status="neutral" label="Archived" dot={false} />}
          </div>
          <div className="truncate text-xs text-grey-500">
            {opts.showClient && p.client ? p.client.name : BASIS_LABEL[p.metrics.basis]}
            {p.code ? ` · ${p.code}` : ''}
          </div>
        </div>
      ),
    },
    {
      // The verdict, on the list, next to the name — the answer before the
      // evidence. The detail page has computed AT RISK / WATCH / HEALTHY /
      // THIN MARGIN / INTERNAL since day one; this screen used to make you
      // derive it yourself from a burn column whose denominator, unit and
      // period changed row to row, and six rows offered no health signal at
      // all beyond a ghost "Set budget" button. Same function, same words,
      // same colours as the header pill (see verdict.ts#verdictClass).
      key: 'status',
      header: <span title={STATUS_TITLE}>Status</span>,
      sortable: true,
      sortValue: (p) => verdictScore(p.metrics),
      className: 'w-[7rem]',
      render: (p) => {
        const v = verdictClass(p.metrics);
        return <VerdictChip size="sm" tone={v.tone} label={v.label} title={verdictSummary(p.metrics)} />;
      },
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      sortValue: (p) => p.type,
      render: (p) => <TypeBadge type={p.type} />,
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      sortable: true,
      sortValue: (p) => p.metrics.hours,
      render: (p) => formatHours(p.metrics.hours, { fixed: true }),
    },
    {
      key: 'revenue',
      header: 'Revenue',
      align: 'right',
      sortable: true,
      // Non-billable rows print "—" here; a blank has no place on the ramp, so
      // it sorts to the bottom rather than tying with a genuine $0.
      sortValue: (p) => (p.metrics.basis === 'NON_BILLABLE' ? null : p.metrics.revenue),
      render: (p) =>
        p.metrics.basis === 'NON_BILLABLE' ? (
          <span className="text-grey-400" title={revenueTitle(p.metrics.basis)}>
            —
          </span>
        ) : (
          // The basis is printed, not just hovered: three of them share this
          // column, and a fixed-bid contract value read as hours-at-rate makes
          // the margin beside it unreadable. Free — the name cell is already
          // two lines tall.
          <div className="leading-tight" title={revenueTitle(p.metrics.basis)}>
            <div>{formatMoney(p.metrics.revenue, { whole: true })}</div>
            <div className="text-[0.625rem] font-normal text-grey-400">
              {REVENUE_SOURCE_TAG[p.metrics.revenueSource]}
            </div>
          </div>
        ),
    },
    {
      key: 'cost',
      header: <span title={COST_TITLE}>Cost</span>,
      align: 'right',
      sortable: true,
      sortValue: (p) => p.metrics.cost,
      render: (p) => formatMoney(p.metrics.cost, { whole: true }),
    },
    {
      key: 'burn',
      header: <span title={BURN_TITLE}>Budget burn</span>,
      sortable: true,
      // No budget = no burn: null, so "Set budget" rows sink in BOTH
      // directions instead of impersonating the lowest burn in the org.
      sortValue: (p) => (p.prepaid ? prepaidPct(p.prepaid) : (p.metrics.burn?.pct ?? null)),
      className: 'w-40',
      render: (p) =>
        p.prepaid ? (
          <PrepaidBar prepaid={p.prepaid} className="min-w-[8rem]" />
        ) : p.metrics.burn ? (
          <BudgetBar burn={p.metrics.burn} className="min-w-[8rem]" />
        ) : opts.onSetBudget ? (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-grey-300 px-2 py-1 text-xs font-semibold text-grey-500 transition-colors hover:border-accent hover:text-accent"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              opts.onSetBudget!(p);
            }}
          >
            <Target className="h-3.5 w-3.5" aria-hidden />
            Set budget
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-grey-400">
            <Target className="h-3.5 w-3.5" aria-hidden />
            No budget set
          </span>
        ),
    },
    {
      key: 'margin',
      header: 'Margin',
      align: 'right',
      sortable: true,
      // Undefined margin (no recognised revenue) sinks — see DataTable. As
      // -999 it used to win "worst margin first" outright, hiding the three
      // hosting agreements at −606.6%, −178.2% and −64.2% below two internal rows.
      sortValue: (p) => p.metrics.margin,
      render: (p) => <MarginText margin={p.metrics.margin} />,
    },
    ...(opts.rowActions
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">Row actions</span>,
            align: 'right' as const,
            className: 'w-[3.5rem] px-2',
            render: (p: AgreementTableRow) => <AgreementRowActions agreement={p} />,
          },
        ]
      : []),
  ];
}

// ---------------------------------------------------------------------------
// Row actions
// ---------------------------------------------------------------------------

/**
 * Start a timer, or bill the work, without leaving the list.
 *
 * Harvest's one genuinely better list behaviour is that an agreement row is a
 * thing you can act on — you start tracking from it. Ours was read-only: every
 * row was a link and nothing else, so "I need to log time against this" meant
 * list → agreement → Time → picker. The two actions here are the two things you
 * actually do from a triage screen, and they are revealed on hover and on
 * keyboard focus so the resting row stays a clean table of numbers.
 */
function AgreementRowActions({ agreement: p }: { agreement: AgreementTableRow }) {
  const startTimer = useStartTimer();
  const stop = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <div
      // Present in the DOM always (so Tab reaches them), painted on hover or
      // focus. `focus-within` is what stops a keyboard user tabbing into an
      // invisible button.
      className="flex items-center justify-end gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100"
    >
      {/* No per-row "invoice unbilled" link: hourly invoices are raised by
          amount here, never by gathering hours (Jesse, 2026-08-22). */}
      {p.isActive && (
        <button
          type="button"
          disabled={startTimer.isPending}
          title={`Start a timer on ${p.name}`}
          aria-label={`Start a timer on ${p.name}`}
          onClick={(e) => {
            stop(e);
            startTimer.mutate(
              { agreementId: p.id, date: new Date().toLocaleDateString('en-CA') },
              {
                onSuccess: () => toast.success(`Timer running on ${p.name}.`),
                onError: (error) =>
                  toast.error(
                    error instanceof Error ? error.message : `Couldn’t start a timer on ${p.name}.`,
                  ),
              },
            );
          }}
          className="rounded-md p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-primary focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed"
        >
          {startTimer.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Play className="h-4 w-4" aria-hidden />
          )}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Footer — the two-line rollup, identical wherever the table is used
// ---------------------------------------------------------------------------

export function AgreementsTotalsRow({
  totals,
  rowActions = false,
  selectable = false,
}: {
  totals: AgreementsRollup;
  /** Keep the footer's cell count equal to the header's — a short footer row
      leaves the totals band stopping dead before the last column. */
  rowActions?: boolean;
  /** A selection column shifts every footer cell one right; this pads it. */
  selectable?: boolean;
}) {
  return (
    <>
      <tr>
        {selectable && <td />}
        <td className="px-3 py-2.5 text-caption uppercase tracking-wider text-grey-500">
          {/* Capped and wrapping: this is the first column, and its
              min-content width is what pushes the money column off a narrow
              container (see the column-cap note in CLAUDE.md). */}
          <div className="max-w-[15rem]">
            <div>
              Client work · {totals.client.count} agreement{totals.client.count === 1 ? '' : 's'}
            </div>
            {/* ⚠ THE SHORT QUALIFIER STAYS; THE SENTENCE MOVED BEHIND THE (i).
                Three bases add up here and "revenue" means CASH elsewhere in
                the app, so the total must still say which substance it is — six
                words, not a sentence. */}
            <div className="mt-0.5 font-normal normal-case tracking-normal text-grey-400">
              {rollupBasisNote(totals.sources)}
              <InfoHint label="Revenue basis" width={320}>
                <RollupBasisInfo sources={totals.sources} />
              </InfoHint>
            </div>
          </div>
        </td>
        {/* status */}
        <td />
        {/* type */}
        <td />
        <td className="num-cell px-3 py-2.5">{formatHours(totals.client.hours, { fixed: true })}</td>
        <td className="num-cell px-3 py-2.5">
          {formatMoney(totals.client.revenue, { whole: true })}
        </td>
        <td className="num-cell px-3 py-2.5">{formatMoney(totals.client.cost, { whole: true })}</td>
        <td />
        <td className="num-cell px-3 py-2.5">
          <MarginText margin={totals.clientMargin} />
        </td>
        {rowActions && <td />}
      </tr>
      {totals.internal.count > 0 && (
        <tr className="border-t border-grey-200 text-grey-500">
          {selectable && <td />}
          <td className="px-3 py-2 text-caption font-normal normal-case tracking-normal">
            {/* "fully loaded" is the qualifier that tells this margin from the
                one above it, so it stays. What it MEANS is behind the (i). */}
            incl. {totals.internal.count} internal · fully loaded
            <InfoHint label="Fully loaded" width={320}>
              <p>
                The row above is client work only. This one adds the{' '}
                {totals.internal.count} internal / non-billable agreement
                {totals.internal.count === 1 ? '' : 's'} —{' '}
                <span className="num font-semibold text-secondary">
                  {formatMoney(totals.internal.cost, { whole: true })}
                </span>{' '}
                of cost that earns no revenue — so the margin is struck on client revenue against
                every hour the business actually spent.
              </p>
              <p className="mt-2">
                A blended margin that quietly absorbs non-billable cost is how this footer once
                printed −14.7% under a header saying 43.4%; that is why the two are totalled
                separately rather than added together.
              </p>
            </InfoHint>
          </td>
          <td />
          <td />
          <td className="num-cell px-3 py-2 text-xs font-normal">
            {formatHours(totals.all.hours, { fixed: true })}
          </td>
          <td className="num-cell px-3 py-2 text-xs font-normal">
            {formatMoney(totals.all.revenue, { whole: true })}
          </td>
          <td className="num-cell px-3 py-2 text-xs font-normal">
            {formatMoney(totals.all.cost, { whole: true })}
          </td>
          <td />
          <td className="num-cell px-3 py-2 text-xs">
            <MarginText margin={totals.loadedMargin} />
          </td>
          {rowActions && <td />}
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Desktop table
// ---------------------------------------------------------------------------

export interface AgreementsTableProps {
  rows: AgreementTableRow[];
  totals: AgreementsRollup;
  showClient?: boolean;
  sort?: DataTableSort;
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  onSortChange?: (sort: DataTableSort) => void;
  onSetBudget?: (row: AgreementTableRow) => void;
  onRowClick?: (row: AgreementTableRow) => void;
  /**
   * Where a row leads. Defaults to `/agreements/:id`; a client's Agreements tab
   * passes a builder that carries `?from=client`, so the agreement page knows
   * to send the reader back to the client rather than to the portfolio.
   */
  rowHref?: (row: AgreementTableRow) => string;
  /** Per-row start-timer / quick-invoice column. */
  rowActions?: boolean;
  /**
   * Bulk selection (Jesse, 2026-08-20: bulk archive). Providing BOTH turns on
   * a leading checkbox column; the name column keeps the row link via
   * `isRowLink`, because a checkbox inside the row's anchor would make every
   * tick a navigation. Desktop only — the mobile cards are a triage surface,
   * and bulk cleanup is a desktop act.
   */
  selected?: ReadonlySet<string>;
  onToggleSelect?: (id: string, checked: boolean) => void;
  onToggleSelectAll?: (checked: boolean) => void;
  empty?: ReactNode;
  className?: string;
  /** Group rows under a header per client, clients A→Z (the /agreements list).
   *  The name cell then prints the basis instead of repeating the client. */
  groupByClient?: boolean;
}

/** The one client grouping — the table, the phone cards and the CSV share it. */
export const CLIENT_GROUP: DataTableGroup<AgreementTableRow> = {
  key: (p) => p.client?.id ?? '',
  label: (p) => p.client?.name ?? 'No client',
};

export function AgreementsTable({
  rows,
  totals,
  showClient = false,
  sort,
  defaultSort,
  onSortChange,
  onSetBudget,
  onRowClick,
  rowHref = (p) => `/agreements/${p.id}`,
  rowActions = false,
  selected,
  onToggleSelect,
  onToggleSelectAll,
  empty,
  className,
  groupByClient = false,
}: AgreementsTableProps) {
  const selectable = selected !== undefined && onToggleSelect !== undefined;
  const requireHubLink = useOrgSettings().data?.requireHubLink === true;
  const base = agreementColumns({
    showClient: showClient && !groupByClient,
    onSetBudget,
    rowActions,
    requireHubLink,
  });
  const columns = selectable
    ? [
        {
          key: 'select',
          className: 'w-8',
          header: (
            <input
              type="checkbox"
              aria-label="Select every agreement in view"
              className="h-4 w-4 cursor-pointer rounded border-grey-300 accent-accent"
              checked={rows.length > 0 && rows.every((p) => selected!.has(p.id))}
              onChange={(e) => onToggleSelectAll?.(e.target.checked)}
            />
          ),
          render: (p: AgreementTableRow) => (
            <input
              type="checkbox"
              aria-label={`Select ${p.name}`}
              className="h-4 w-4 cursor-pointer rounded border-grey-300 accent-accent"
              checked={selected!.has(p.id)}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onToggleSelect!(p.id, e.target.checked)}
            />
          ),
        },
        // The link moves to the name column — see `isRowLink` on DataTable.
        ...base.map((col, i) => (i === 0 ? { ...col, isRowLink: true } : col)),
      ]
    : base;
  return (
    <Card className={className}>
      <DataTable
        columns={columns}
        data={rows}
        rowKey={(p) => p.id}
        rowHref={rowHref}
        rowLabel={(p) => (p.client ? `${p.name} — ${p.client.name}` : p.name)}
        onRowClick={onRowClick}
        // How many agreements you can see at once IS the feature on a triage
        // list: at the old row height only 10 of 18 fit above the fold, so the
        // bottom third of the portfolio — where the losses live — was invisible
        // on arrival.
        density="compact"
        sort={sort}
        defaultSort={defaultSort}
        onSortChange={onSortChange}
        group={groupByClient ? CLIENT_GROUP : undefined}
        empty={empty}
        footer={
          rows.length > 0 ? (
            <AgreementsTotalsRow totals={totals} rowActions={rowActions} selectable={selectable} />
          ) : undefined
        }
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Mobile — cards, because seven columns cannot survive 390px
// ---------------------------------------------------------------------------

/**
 * The phone card carries Cost.
 *
 * It used to print Hours / Revenue / Margin and drop Cost entirely, so the one
 * number Harvest cannot show you disappeared the moment you picked up a phone.
 * Four figures in a 2×2 block fit at 390px where four columns do not.
 */
export function AgreementMobileCard({
  agreement: p,
  showClient = false,
  onSetBudget,
  href,
}: {
  agreement: AgreementTableRow;
  showClient?: boolean;
  onSetBudget?: (row: AgreementTableRow) => void;
  /** Same contract as the table's `rowHref`; see AgreementsTableProps. */
  href?: (row: AgreementTableRow) => string;
}) {
  const m = p.metrics;
  const v = verdictClass(m);
  const requireHubLink = useOrgSettings().data?.requireHubLink === true;
  return (
    <Card variant="hover" padding="sm" className={cn(!p.isActive && 'opacity-70')}>
      <Link to={href ? href(p) : `/agreements/${p.id}`} className="block">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-bold text-secondary">{p.name}</p>
            <p className="mt-0.5 truncate text-caption text-grey-500">
              {showClient && p.client ? p.client.name : BASIS_LABEL[m.basis]}
              {p.code ? ` · ${p.code}` : ''}
            </p>
          </div>
          {/* The verdict leads on the phone too — the desktop STATUS column
              cannot just evaporate at 390px, or the two layouts answer
              different questions. */}
          <div className="flex shrink-0 flex-col items-end gap-1">
            <VerdictChip size="sm" tone={v.tone} label={v.label} title={verdictSummary(m)} />
            <div className="flex items-center gap-1.5">
              {(p.hubProjectIds?.length ?? 0) > 0 ? (
                <HubBadge />
              ) : requireHubLink && p.isActive ? (
                <UnlinkedBadge />
              ) : null}
              <TypeBadge type={p.type} />
              {!p.isActive && <StatusPill status="neutral" label="Archived" dot={false} />}
            </div>
          </div>
        </div>

        <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5">
          <Figure label="Hours" value={formatHours(m.hours, { fixed: true })} />
          <Figure
            // The phone says which basis too — but UNDER the figure, the way
            // the desktop column does, not appended to the label. As a label
            // ("Revenue · hours × rate") it left 46px for the money and every
            // hourly row on the phone rendered its revenue as "$…".
            label="Revenue"
            note={m.basis === 'NON_BILLABLE' ? undefined : REVENUE_SOURCE_TAG[m.revenueSource]}
            value={m.basis === 'NON_BILLABLE' ? '—' : formatMoney(m.revenue, { whole: true })}
            title={revenueTitle(m.basis)}
          />
          <Figure label="Cost" value={formatMoney(m.cost, { whole: true })} title={COST_TITLE} />
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-caption text-grey-500">Margin</dt>
            <dd className="text-body-sm">
              <MarginText margin={m.margin} />
            </dd>
          </div>
        </dl>
      </Link>

      <div className="mt-2.5 border-t border-grey-100 pt-2.5">
        {p.prepaid ? (
          <PrepaidBar prepaid={p.prepaid} variant="full" />
        ) : m.burn ? (
          <BudgetBar burn={m.burn} variant="full" />
        ) : onSetBudget ? (
          <button
            type="button"
            onClick={() => onSetBudget(p)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-grey-300 px-2.5 py-1 text-xs font-semibold text-grey-500 transition-colors hover:border-accent hover:text-accent"
          >
            <Target className="h-3.5 w-3.5" aria-hidden />
            Set a budget
          </button>
        ) : (
          <p className="text-caption text-grey-500">No budget set — no burn to track against.</p>
        )}
      </div>
    </Card>
  );
}

function Figure({
  label,
  value,
  title,
  /** Printed under the figure — the basis word, never inside the label. */
  note,
}: {
  label: string;
  value: string;
  title?: string;
  note?: string;
}) {
  return (
    <div title={title}>
      <div className="flex items-baseline justify-between gap-2">
        <dt className="shrink-0 text-caption text-grey-500">{label}</dt>
        <dd className="num truncate text-body-sm font-semibold text-secondary">{value}</dd>
      </div>
      {note && <dd className="-mt-0.5 text-right text-[0.625rem] text-grey-400">{note}</dd>}
    </div>
  );
}

/** The mobile footer card — same two-line rollup as the desktop table footer. */
export function AgreementsTotalsCard({ totals }: { totals: AgreementsRollup }) {
  return (
    <Card padding="sm">
      <p className="text-caption font-bold uppercase tracking-wider text-grey-500">
        Client work · {totals.client.count} agreement{totals.client.count === 1 ? '' : 's'}
      </p>
      {/* Same short qualifier as the desktop footer, and the same (i) behind
          it — a phone is exactly where a `title` was unreadable. */}
      <p className="mt-0.5 text-caption text-grey-400">
        {rollupBasisNote(totals.sources)}
        <InfoHint label="Revenue basis" width={320}>
          <RollupBasisInfo sources={totals.sources} />
        </InfoHint>
      </p>
      <dl className="mt-2 grid grid-cols-4 gap-2 text-body-sm">
        <div>
          <dt className="text-caption text-grey-500">Hours</dt>
          <dd className="num font-bold text-secondary">
            {formatHours(totals.client.hours, { fixed: true })}
          </dd>
        </div>
        <div>
          <dt className="text-caption text-grey-500">Revenue</dt>
          <dd className="num font-bold text-secondary">
            {formatMoney(totals.client.revenue, { whole: true })}
          </dd>
        </div>
        <div>
          <dt className="text-caption text-grey-500">Cost</dt>
          <dd className="num font-bold text-secondary">
            {formatMoney(totals.client.cost, { whole: true })}
          </dd>
        </div>
        <div>
          <dt className="text-caption text-grey-500">Margin</dt>
          <dd className="font-bold">
            <MarginText margin={totals.clientMargin} />
          </dd>
        </div>
      </dl>
      {totals.internal.count > 0 && (
        <p className="mt-2 border-t border-grey-100 pt-2 text-caption text-grey-500">
          Plus {formatMoney(totals.internal.cost, { whole: true })} internal · excluded
          <InfoHint label="Internal cost" width={300}>
            <p>
              {totals.internal.count} internal / non-billable agreement
              {totals.internal.count === 1 ? '' : 's'} cost{' '}
              <span className="num font-semibold text-secondary">
                {formatMoney(totals.internal.cost, { whole: true })}
              </span>{' '}
              and earn no revenue, so they are totalled separately and left out of the margin above.
              A blended margin that quietly absorbs non-billable cost is how this footer once
              printed −14.7% under a header saying 43.4%.
            </p>
          </InfoHint>
        </p>
      )}
    </Card>
  );
}
